// Client-side transaction signing + broadcast via the zero-knowledge RPC gateway.
import { JsonRpcProvider, Wallet, parseEther, parseUnits, Contract, AbiCoder, SigningKey, getBytes, hexlify } from "ethers";
import {
  Connection, PublicKey, Keypair, SystemProgram, Transaction, LAMPORTS_PER_SOL,
} from "@solana/web3.js";
import {
  getAssociatedTokenAddress, createTransferInstruction, createAssociatedTokenAccountInstruction, getAccount,
} from "@solana/spl-token";
import * as bitcoin from "bitcoinjs-lib";
import bs58check from "bs58check";
import { Buffer } from "buffer";
import { API } from "./api";
import { api } from "./api";
import { getChain } from "./chains";

async function sendEvm(chainId, account, to, amount) {
  const provider = new JsonRpcProvider(`${API}/rpc/evm/${chainId}`);
  const wallet = new Wallet(account.evm.privateKey, provider);
  const tx = await wallet.sendTransaction({ to, value: parseEther(String(amount)) });
  return tx.hash;
}

async function sendSol(account, to, amount) {
  const conn = new Connection(`${API}/rpc/sol`, "confirmed");
  const from = Keypair.fromSecretKey(Uint8Array.from(account.sol.secretKey));
  const { blockhash } = await conn.getLatestBlockhash();
  const tx = new Transaction({ recentBlockhash: blockhash, feePayer: from.publicKey }).add(
    SystemProgram.transfer({
      fromPubkey: from.publicKey,
      toPubkey: new PublicKey(to),
      lamports: Math.round(Number(amount) * LAMPORTS_PER_SOL),
    })
  );
  tx.sign(from);
  const sig = await conn.sendRawTransaction(tx.serialize());
  return sig;
}

async function sendBtc(account, to, amount) {
  const addr = account.btc.address;
  const { data: utxos } = await api.get(`/rpc/btc/address/${addr}/utxo`);
  if (!utxos || utxos.length === 0) throw new Error("No spendable UTXOs / zero balance");
  let feeRate = 8;
  try {
    const { data: fees } = await api.get(`/rpc/btc/v1/fees/recommended`);
    feeRate = fees.halfHourFee || feeRate;
  } catch {}

  const network = bitcoin.networks.bitcoin;
  const pubkey = Buffer.from(account.btc.publicKey, "hex");
  const p2wpkh = bitcoin.payments.p2wpkh({ pubkey, network });
  const psbt = new bitcoin.Psbt({ network });

  const target = Math.round(Number(amount) * 1e8);
  let inputSum = 0;
  const chosen = [];
  for (const u of utxos.sort((a, b) => b.value - a.value)) {
    chosen.push(u);
    inputSum += u.value;
    if (inputSum >= target + 300 * feeRate) break;
  }
  const estFee = (10 + chosen.length * 68 + 2 * 31) * feeRate;
  const change = inputSum - target - estFee;
  if (change < 0) throw new Error("Insufficient BTC balance for amount + fee");

  chosen.forEach((u) => {
    psbt.addInput({ hash: u.txid, index: u.vout, witnessUtxo: { script: p2wpkh.output, value: u.value } });
  });
  psbt.addOutput({ address: to, value: target });
  if (change > 546) psbt.addOutput({ address: addr, value: change });

  const sk = new SigningKey("0x" + account.btc.privateKey);
  const signer = {
    publicKey: pubkey,
    sign: (hash) => {
      const sig = sk.sign(hexlify(hash));
      return Buffer.from(sig.r.slice(2) + sig.s.slice(2), "hex");
    },
  };
  chosen.forEach((_, i) => psbt.signInput(i, signer));
  psbt.finalizeAllInputs();
  const rawHex = psbt.extractTransaction().toHex();
  const { data: txid } = await api.post(`/rpc/btc/tx`, rawHex, { headers: { "Content-Type": "text/plain" } });
  return typeof txid === "string" ? txid.trim() : txid;
}

async function sendTron(account, to, amount) {
  const owner = account.tron.address;
  const sun = Math.round(Number(amount) * 1e6);
  const { data: created } = await api.post(`/rpc/tron/wallet/createtransaction`, {
    owner_address: owner,
    to_address: to,
    amount: sun,
    visible: true,
  });
  if (created.Error || !created.txID) throw new Error(created.Error || "Failed to build TRX transaction");
  const sk = new SigningKey("0x" + account.tron.privateKey.replace(/^0x/, ""));
  const sig = sk.sign("0x" + created.txID);
  const signature = sig.r.slice(2) + sig.s.slice(2) + (sig.yParity === 1 ? "01" : "00");
  const { data: result } = await api.post(`/rpc/tron/wallet/broadcasttransaction`, {
    ...created,
    signature: [signature],
  });
  if (!result.result) throw new Error(result.message ? Buffer.from(result.message, "hex").toString() : "Broadcast failed");
  return created.txID;
}

export async function sendTransaction(chain, account, to, amount) {
  if (chain.family === "evm") return sendEvm(chain.id, account, to, amount);
  if (chain.family === "sol") return sendSol(account, to, amount);
  if (chain.family === "btc") return sendBtc(account, to, amount);
  if (chain.family === "tron") return sendTron(account, to, amount);
  throw new Error("Unsupported chain");
}

// ---------- Token transfers ----------
async function sendErc20(chainId, account, token, to, amount) {
  const provider = new JsonRpcProvider(`${API}/rpc/evm/${chainId}`);
  const wallet = new Wallet(account.evm.privateKey, provider);
  const contract = new Contract(token.address, ["function transfer(address to, uint256 value) returns (bool)"], wallet);
  const value = parseUnits(String(amount), token.decimals);
  const tx = await contract.transfer(to, value);
  return tx.hash;
}

function tronBase58ToEvmHex(base58) {
  const bytes = bs58check.decode(base58); // 0x41 + 20 bytes
  return "0x" + Buffer.from(bytes.slice(1)).toString("hex");
}

async function sendTrc20(account, token, toBase58, amount) {
  const value = parseUnits(String(amount), token.decimals);
  const toHex = tronBase58ToEvmHex(toBase58);
  const parameter = AbiCoder.defaultAbiCoder().encode(["address", "uint256"], [toHex, value]).slice(2);
  const { data: built } = await api.post(`/rpc/tron/wallet/triggersmartcontract`, {
    owner_address: account.tron.address,
    contract_address: token.address,
    function_selector: "transfer(address,uint256)",
    parameter,
    fee_limit: 100000000,
    call_value: 0,
    visible: true,
  });
  const tx = built.transaction;
  if (!tx || !tx.txID) {
    const m = built.result && built.result.message ? Buffer.from(built.result.message, "hex").toString() : "Failed to build TRC20 transaction";
    throw new Error(m);
  }
  const sk = new SigningKey("0x" + account.tron.privateKey.replace(/^0x/, ""));
  const sig = sk.sign("0x" + tx.txID);
  const signature = sig.r.slice(2) + sig.s.slice(2) + (sig.yParity === 1 ? "01" : "00");
  const { data: result } = await api.post(`/rpc/tron/wallet/broadcasttransaction`, { ...tx, signature: [signature] });
  if (!result.result) throw new Error(result.message ? Buffer.from(result.message, "hex").toString() : "Broadcast failed");
  return tx.txID;
}

async function sendSpl(account, token, to, amount) {
  const conn = new Connection(`${API}/rpc/sol`, "confirmed");
  const from = Keypair.fromSecretKey(Uint8Array.from(account.sol.secretKey));
  const mint = new PublicKey(token.address);
  const toPubkey = new PublicKey(to);
  const fromAta = await getAssociatedTokenAddress(mint, from.publicKey);
  const toAta = await getAssociatedTokenAddress(mint, toPubkey);

  const { blockhash } = await conn.getLatestBlockhash();
  const tx = new Transaction({ recentBlockhash: blockhash, feePayer: from.publicKey });

  let needsAta = false;
  try {
    await getAccount(conn, toAta);
  } catch {
    needsAta = true;
  }
  if (needsAta) {
    tx.add(createAssociatedTokenAccountInstruction(from.publicKey, toAta, toPubkey, mint));
  }
  const value = BigInt(Math.round(Number(amount) * 10 ** token.decimals));
  tx.add(createTransferInstruction(fromAta, toAta, from.publicKey, value));
  tx.sign(from);
  const sig = await conn.sendRawTransaction(tx.serialize());
  return sig;
}

// Unified dispatcher: sends a native coin or a token.
export async function sendAsset(asset, account, to, amount) {
  if (asset.kind === "native") return sendTransaction(getChain(asset.chain), account, to, amount);
  const t = asset.token;
  if (t.std === "erc20") return sendErc20(asset.chain, account, t, to, amount);
  if (t.std === "trc20") return sendTrc20(account, t, to, amount);
  if (t.std === "spl") return sendSpl(account, t, to, amount);
  throw new Error("Unsupported token");
}
